import * as Sentry from '@sentry/react';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Routes, Route } from 'react-router-dom';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  lazy,
  Suspense,
  useState,
} from 'react';
import { useAffiliateTracking } from '@/hooks/useAffiliateTracking';
import { usePageTracking } from '@/hooks/usePageTracking';
import { useClientTracking } from '@/hooks/useClientTracking';
import { getClientConfig, type ClientData } from '@/lib/client';
import { ErrorFallback } from '@/components/ErrorFallback';
import ScrollToTop from '@/components/ScrollToTop';
import { MarketingAuthRedirect } from '@/components/MarketingAuthRedirect';
import { scheduleIdleTask } from '@/lib/scheduleIdleTask';
import Index from './pages/Index';

const FAQ = lazy(() => import('./pages/FAQ'));
const Plans = lazy(() => import('./pages/Plans'));
const PlanChoiceSummary = lazy(() => import('./pages/PlanChoiceSummary'));
const WhyPerchMobile = lazy(() => import('./pages/WhyPerchMobile'));
const Contact = lazy(() => import('./pages/Contact'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const NotFound = lazy(() => import('./pages/NotFound'));
const PerchInternship = lazy(() => import('./pages/PerchInternship'));
const Affiliates = lazy(() => import('./pages/Affiliates'));

const queryClient = new QueryClient();

// Signals whether the client attribution lookup has settled. Pages that write
// the cart (PlanChoiceSummary) gate their Continue action on this being true.
export const ClientResolvedContext = createContext(false);
export function useClientResolved() {
  return useContext(ClientResolvedContext);
}

// Exposes the DB-resolved client record once useClientTracking has settled.
// null before resolution or when no valid client row was found.
export const ClientDataContext = createContext<ClientData | null>(null);
export function useClientContext(): ClientData | null {
  return useContext(ClientDataContext);
}

function AppTrackers({ onClientResolved }: { onClientResolved: () => void }) {
  useAffiliateTracking();
  usePageTracking();
  const { resolved } = useClientTracking();

  useEffect(() => {
    if (resolved) onClientResolved();
  }, [resolved, onClientResolved]);

  return null;
}

function IntercomManager() {
  useEffect(() => {
    let cancelled = false;
    const cancelIdleTask = scheduleIdleTask(() => {
      void import('@intercom/messenger-js-sdk').then(({ default: Intercom }) => {
        if (!cancelled) {
          Intercom({ app_id: import.meta.env.VITE_INTERCOM_APP_ID });
        }
      });
    });

    return () => {
      cancelled = true;
      cancelIdleTask();
    };
  }, []);
  return null;
}

function PrerenderSignal() {
  useEffect(() => {
    const timer = setTimeout(() => {
      document.dispatchEvent(new Event('prerender-ready'));
    }, 1000);
    return () => clearTimeout(timer);
  }, []);
  return null;
}

function AppWithTrackers() {
  const [clientResolved, setClientResolved] = useState(false);
  const [clientData, setClientData] = useState<ClientData | null>(null);

  const handleClientResolved = useCallback(() => {
    setClientResolved(true);
    setClientData(getClientConfig());
  }, []);

  return (
    <ClientResolvedContext.Provider value={clientResolved}>
      <ClientDataContext.Provider value={clientData}>
        <BrowserRouter>
          <ScrollToTop />
          <AppTrackers onClientResolved={handleClientResolved} />
          <PrerenderSignal />
          <MarketingAuthRedirect />
          <IntercomManager />
          <Sentry.ErrorBoundary
            fallback={({ error, resetError }) => (
              <ErrorFallback error={error} resetError={resetError} />
            )}
            showDialog={false}
            beforeCapture={(scope) => {
              scope.setTag('location', 'root');
            }}
          >
            <Suspense fallback={null}>
              <Routes>
                <Route path='/' element={<Index />} />
                <Route path='/faq' element={<FAQ />} />
                <Route path='/plans' element={<Plans />} />
                <Route path='/plan-summary' element={<PlanChoiceSummary />} />
                <Route path='/why-perch-mobile' element={<WhyPerchMobile />} />
                <Route path='/contact' element={<Contact />} />
                <Route
                  path='/terms-and-conditions'
                  element={<TermsAndConditions />}
                />
                <Route path='/privacy-policy' element={<PrivacyPolicy />} />
                <Route path='/perch-internship' element={<PerchInternship />} />
                <Route path='/affiliates' element={<Affiliates />} />
                <Route
                  path='/credit-building'
                  element={<Navigate to='/plans' replace />}
                />
                <Route
                  path='/sentry-test'
                  element={<Navigate to='/' replace />}
                />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path='*' element={<NotFound />} />
              </Routes>
            </Suspense>
          </Sentry.ErrorBoundary>
        </BrowserRouter>
      </ClientDataContext.Provider>
    </ClientResolvedContext.Provider>
  );
}

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AppWithTrackers />
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
